import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, User, FileText } from 'lucide-react';
import AdminLayout from './AdminLayout';

interface AuditLog {
  id: string;
  adminUserId: string;
  adminUser?: { username: string; displayName: string | null };
  action: string;
  targetType: string;
  targetId?: string;
  createdAt: string;
}

export default function AdminSettings() {
  const { data: auditLogs, isLoading } = useQuery<AuditLog[]>({
    queryKey: ['/api/admin/audit-logs'],
  });

  const getActionColor = (action: string) => {
    if (action.includes('approved')) return 'bg-green-500';
    if (action.includes('rejected')) return 'bg-red-500';
    if (action.includes('flagged')) return 'bg-yellow-500';
    if (action.includes('created')) return 'bg-blue-500';
    if (action.includes('updated')) return 'bg-purple-500';
    if (action.includes('deleted')) return 'bg-red-500';
    return 'bg-gray-500';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-page-title">Settings & Audit Log</h1>
          <p className="text-muted-foreground">System settings and admin activity history</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Recent Admin Activity</CardTitle>
            <CardDescription>History of all admin actions for accountability</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : auditLogs?.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No audit logs yet</p>
            ) : (
              <div className="space-y-4">
                {auditLogs?.map((log) => (
                  <div 
                    key={log.id} 
                    className="flex items-start gap-4 p-3 rounded-lg bg-muted/50"
                    data-testid={`audit-log-${log.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">
                          {log.adminUser?.displayName || log.adminUser?.username || 'Admin'}
                        </span>
                        <Badge className={getActionColor(log.action)}>{log.action}</Badge>
                        <Badge variant="outline">{log.targetType}</Badge>
                        {log.targetId && (
                          <span className="text-xs text-muted-foreground">ID: {log.targetId.slice(0, 8)}...</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {new Date(log.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
